import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Crown, TrendingUp, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccessPage({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  const pollInterval = 2000;

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    pollPaymentStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt >= maxAttempts) {
      setStatus("error");
      toast.error("Impossible de vérifier le paiement. Veuillez contacter le support.");
      return;
    }

    setAttempts(attempt + 1);

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        toast.success("Paiement confirmé ! Bienvenue chez Premium !");
        
        // Refresh user data
        const userRes = await axios.get(`${API}/auth/me`);
        setUser(userRes.data);
      } else if (response.data.status === "expired") {
        setStatus("error");
        toast.error("Session de paiement expirée");
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
      }
    } catch (error) {
      console.error("Payment status check error:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-card border-border">
        <CardContent className="pt-8 pb-8 text-center">
          {status === "checking" && (
            <>
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold font-manrope mb-2">Vérification du paiement</h1>
              <p className="text-muted-foreground mb-4">
                Veuillez patienter pendant que nous confirmons votre paiement...
              </p>
              <p className="text-xs text-muted-foreground">
                Tentative {attempts}/{maxAttempts}
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="w-6 h-6 text-amber-500" />
                <h1 className="text-2xl font-bold font-manrope">Bienvenue chez Premium !</h1>
              </div>
              <p className="text-muted-foreground mb-6">
                Votre paiement a été confirmé. Vous avez maintenant accès à toutes les fonctionnalités Premium.
              </p>
              
              <div className="bg-muted/50 rounded-sm p-4 mb-6 text-left">
                <h3 className="font-semibold mb-2">Vos avantages</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Briefings hebdomadaires illimités
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Questions Q&A illimitées
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Accès prioritaire aux nouvelles fonctionnalités
                  </li>
                </ul>
              </div>
              
              <Button 
                data-testid="go-dashboard-btn"
                onClick={() => navigate("/dashboard")}
                className="w-full gap-2 rounded-sm"
              >
                <TrendingUp className="w-4 h-4" />
                Accéder au tableau de bord
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">⚠️</span>
              </div>
              <h1 className="text-2xl font-bold font-manrope mb-2">Problème de paiement</h1>
              <p className="text-muted-foreground mb-6">
                Nous n'avons pas pu confirmer votre paiement. Si votre compte a été débité, veuillez contacter le support.
              </p>
              <div className="flex flex-col gap-2">
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="w-full rounded-sm"
                >
                  Retour au tableau de bord
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/settings")}
                  className="w-full"
                >
                  Réessayer
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
